// Bridge to the Android app. MainActivity injects a synchronous
// window.TimepeakNativeBridge into the WebView; its async methods take a callback
// id and the app answers through window.TimepeakNative._resolve({id, value, error}).
// This module installs the same window.TimepeakNative facade the old web app
// had, so the Android app keeps working unchanged.

interface NativeBridge {
  platform(): string;
  appVersion(): string;
  getNotificationPermissionStatus(): string;
  requestNotificationPermission(id: string): void;
  getDeviceToken(id: string): void;
  openSystemNotificationSettings(): void;
  signalLogout(id: string): void;
}

declare global {
  interface Window {
    TimepeakNativeBridge?: NativeBridge;
    TimepeakNative?: NativeFacade;
  }
}

type PermissionStatus = 'granted' | 'denied' | 'default' | 'blocked' | 'unsupported';

interface NativeFacade {
  isNativeApp(): boolean;
  platform(): string;
  appVersion(): string;
  getNotificationPermissionStatus(): PermissionStatus;
  requestNotificationPermission(): Promise<string>;
  getDeviceToken(): Promise<string>;
  openSystemNotificationSettings(): void;
  signalLogout(): Promise<unknown>;
  _resolve(msg: { id: string; value?: unknown; error?: string }): void;
}

const pending = new Map<string, { resolve: (v: any) => void; reject: (e: Error) => void }>();
const bridge = (): NativeBridge | null => (typeof window !== 'undefined' && window.TimepeakNativeBridge) || null;

function call<T>(method: keyof NativeBridge): Promise<T> {
  return new Promise((resolve, reject) => {
    const b = bridge();
    const fn = b?.[method] as ((id: string) => void) | undefined;
    if (!b || typeof fn !== 'function') {
      reject(new Error(`Native bridge method not available: ${String(method)}`));
      return;
    }
    const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    pending.set(id, { resolve, reject });
    try {
      fn.call(b, id);
    } catch (err) {
      pending.delete(id);
      reject(err as Error);
    }
  });
}

export const native: NativeFacade = {
  isNativeApp: () => !!bridge(),
  platform: () => bridge()?.platform() ?? 'web',
  appVersion: () => bridge()?.appVersion() ?? '',
  getNotificationPermissionStatus: () => (bridge()?.getNotificationPermissionStatus() as PermissionStatus) ?? 'unsupported',
  requestNotificationPermission: () => call<string>('requestNotificationPermission'),
  getDeviceToken: () => call<string>('getDeviceToken'),
  openSystemNotificationSettings: () => bridge()?.openSystemNotificationSettings(),
  signalLogout: () => call('signalLogout'),
  _resolve: ({ id, value, error }) => {
    const p = pending.get(id);
    if (!p) return;
    pending.delete(id);
    if (error) p.reject(new Error(error));
    else p.resolve(value);
  }
};

if (typeof window !== 'undefined' && !window.TimepeakNative) {
  window.TimepeakNative = native;
}
